import { Link } from "@tanstack/react-router";
import { Plus, Link as LinkIcon, AppWindow, FolderSync, History } from "lucide-react";
import { useTranslation } from "react-i18next";

import { authClient } from "@/lib/auth-client";
import { useLocale } from "@/hooks/use-locale";
import { Button, PageHeader } from "@h2j-data-team/ui";

function getFirstName(fullName?: string | null): string | null {
  if (!fullName) return null;
  const trimmed = fullName.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

/** The dashboard's page header: greeting title, date subtitle, primary action. */
export function Hero() {
  const { t } = useTranslation("dashboard");
  const { language } = useLocale();
  const session = authClient.useSession();
  const firstName = getFirstName(session.data?.user?.name) ?? "";

  const dateLabel = new Date().toLocaleDateString(language, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <PageHeader
      title={
        firstName
          ? t("hero.welcomeWithName", {
              name: firstName,
              defaultValue: `Welcome back, ${firstName}`,
            })
          : t("hero.welcome", { defaultValue: "Welcome back" })
      }
      description={dateLabel}
      actions={
        <Button asChild size="sm">
          <Link to="/connections" search={{ q: undefined }}>
            <Plus strokeWidth={1.8} />
            {t("hero.newConnection", { defaultValue: "Add connection" })}
          </Link>
        </Button>
      }
    />
  );
}

const quickActionClassName =
  "flex items-center gap-[7px] text-[13px] font-medium text-dim transition-colors hover:text-ink [&_svg]:h-[15px] [&_svg]:w-[15px] [&_svg]:text-faint";

/** Shortcut row under the header — quiet by design; the KPI strip is the loud part. */
export function QuickActions() {
  const { t } = useTranslation("dashboard");

  return (
    <div className="flex flex-wrap items-center gap-6">
      <Link to="/connections" search={{ q: undefined }} className={quickActionClassName}>
        <LinkIcon strokeWidth={1.8} />
        {t("hero.actions.manageConnections", { defaultValue: "Manage Connections" })}
      </Link>
      <Link to="/single-app" search={{ q: undefined }} className={quickActionClassName}>
        <AppWindow strokeWidth={1.8} />
        {t("hero.actions.editApp", { defaultValue: "Edit App" })}
      </Link>
      <Link to="/sync-groups" search={{ q: undefined }} className={quickActionClassName}>
        <FolderSync strokeWidth={1.8} />
        {t("hero.actions.manageSyncGroups", { defaultValue: "Manage Sync Groups" })}
      </Link>
      <Link
        to="/jobs"
        search={{
          type: undefined,
          status: undefined,
          q: undefined,
          dateFrom: undefined,
          dateTo: undefined,
        }}
        className={quickActionClassName}
      >
        <History strokeWidth={1.8} />
        {t("hero.actions.jobHistory", { defaultValue: "Job History" })}
      </Link>
    </div>
  );
}
